import { Box, Flex, ScrollArea, Separator, Text } from "@radix-ui/themes";
import { InfoItem } from "@/components/info-item";
import { useGondolaChangeGroupsData } from "../hooks/useGondolaChangeGroupsData";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  GondolaChange,
  selectSelectedGondolaChangeId,
  selectSelectedTriangulationId,
  setSelectedDetectionId,
  setSelectedGondolaChangeId,
  setSelectedSkuSectionChangeId,
} from "@/entities/gondola-change-groups";
import { Fragment } from "react/jsx-runtime";
import useCameraHandlers from "@/features/annotation/hooks/useCameraHandlers";
import {
  selectSelectedSkuSectionId,
  setGondolaDropdownValueByName,
} from "@/entities/annotation";
import { setGondolaByName } from "@/entities/annotation";
import { useTimelineData } from "@/features/annotation/hooks/useTimelineData";
import { usePlayer } from "@tezzro/player";

export function GondolaChangesList() {
  const {
    selectedGondolaChangeGroup,
    filteredGondolaChanges,
    findSkuSectionChangeWithTriangulationId,
  } = useGondolaChangeGroupsData();
  const dispatch = useAppDispatch();
  const selectedGondolaChangeId = useAppSelector(selectSelectedGondolaChangeId);
  const selectedSkuSectionId = useAppSelector(selectSelectedSkuSectionId);
  const selectedTriangulationId = useAppSelector(selectSelectedTriangulationId);
  const { handleCameraSelect } = useCameraHandlers();
  const player = usePlayer();
  const { highlightGondolaChangesTimeline } = useTimelineData();

  const preselectDetectionWithSameTriangulationId = (
    gondolaChange: GondolaChange
  ) => {
    if (!selectedSkuSectionId || !selectedTriangulationId) {
      return;
    }

    const changeWithTriangulation = findSkuSectionChangeWithTriangulationId(
      selectedSkuSectionId,
      gondolaChange.gondola_change_camera_composite_id,
      selectedTriangulationId
    );

    if (changeWithTriangulation) {
      dispatch(
        setSelectedSkuSectionChangeId(
          changeWithTriangulation?.gondola_change_camera_composite_id
        )
      );

      dispatch(
        setSelectedDetectionId(
          changeWithTriangulation?.detection?.id?.toString() ||
            changeWithTriangulation?.triangulation?.triangulation_id ||
            null
        )
      );
    }
  };

  const onSelectGondolaChange = (gondolaChange: GondolaChange) => {
    if (
      selectedGondolaChangeId !==
      gondolaChange.gondola_change_camera_composite_id
    ) {
      const gondolaName = gondolaChange.gondola_name;

      preselectDetectionWithSameTriangulationId(gondolaChange);

      player.seek(
        gondolaChange?.frame_start_time,
        gondolaChange.camera_local_id
      );
      dispatch(setGondolaByName(gondolaName || null));
      dispatch(setGondolaDropdownValueByName(gondolaName || null));
      handleCameraSelect(gondolaChange.camera_local_id);
      dispatch(
        setSelectedGondolaChangeId(
          gondolaChange.gondola_change_camera_composite_id
        )
      );
      highlightGondolaChangesTimeline(
        gondolaChange.gondola_change_camera_composite_id
      );
    } else {
      dispatch(setSelectedGondolaChangeId(null));
      highlightGondolaChangesTimeline(null);
    }
  };

  return (
    <Flex direction="column" flexGrow="1" flexBasis="1">
      <Separator size="4" />
      <Flex align="center" justify="between" m="2">
        <Text size="2" weight="bold" as="p">
          {selectedGondolaChangeGroup
            ? `Gondola Changes (${filteredGondolaChanges.length})`
            : "Gondola Changes"}
        </Text>
      </Flex>
      <Separator size="4" />
      <ScrollArea type="auto" className="h-0 flex-grow">
        {!selectedGondolaChangeGroup ? (
          <Box p="4">
            <Text size="2" color="gray">
              Select a gondola change group to view its changes
            </Text>
          </Box>
        ) : filteredGondolaChanges.length === 0 ? (
          <Box p="4">
            <Text size="2" color="gray">
              No gondola changes match the current filters
            </Text>
          </Box>
        ) : (
          filteredGondolaChanges.map((gondolaChange: GondolaChange) => (
            <Fragment key={gondolaChange?.gondola_change_camera_composite_id}>
              <Box
                className={`p-2 rounded m-2 ${
                  selectedGondolaChangeId ===
                  gondolaChange?.gondola_change_camera_composite_id
                    ? "bg-blue-100 border border-blue-300"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => onSelectGondolaChange(gondolaChange)}
              >
                <InfoItem
                  label="Camera local ID"
                  value={gondolaChange?.camera_local_id}
                />
                <InfoItem
                  label="Calibration camera ID"
                  value={gondolaChange?.calibration_camera_id}
                />
                <InfoItem
                  label="Calibration ID"
                  value={gondolaChange?.calibration_id}
                />
                <InfoItem
                  label="Gondola name"
                  value={gondolaChange?.gondola_name}
                />
                <InfoItem
                  label="Gondola ID"
                  value={gondolaChange?.gondola_id}
                />
                <InfoItem
                  label="Frame start time"
                  value={gondolaChange?.frame_start_time}
                />
                <InfoItem
                  label="Frame end time"
                  value={gondolaChange?.frame_end_time}
                />
              </Box>
              <Separator size="4" />
            </Fragment>
          ))
        )}
      </ScrollArea>
    </Flex>
  );
}
